'use client';

import { ExclamationTriangleIcon } from '@radix-ui/react-icons';
import { MemberWithUser } from '@zond/api-interfaces';
import { Alert, AlertDescription, AlertTitle, Input, Label } from '@zond/ui';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ConfirmDialog } from '../../../../components/confirm-dialog';
import { useUsers } from '../context/users-context';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentRow: MemberWithUser;
  onDelete: (memberId: number) => Promise<void>;
  isDeleting?: boolean;
}

export function UsersDeleteDialog({ open, onOpenChange, currentRow, onDelete, isDeleting }: Props) {
  const { t } = useTranslation('settings');
  const [value, setValue] = useState('');
  const { setOpen, setCurrentRow } = useUsers();

  const handleDelete = async () => {
    const memberEmail = currentRow.user?.email || currentRow.email;
    if (value.trim() !== memberEmail) return;

    try {
      await onDelete(currentRow.id);
      setValue('');
      setOpen(null);
      setCurrentRow(null);
    } catch (error) {
      // Error will be handled in the parent component
      console.error('Failed to delete member:', error);
    }
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setValue('');
    }
    onOpenChange(isOpen);
  };

  const memberEmail = currentRow.user?.email || currentRow.email;

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={handleOpenChange}
      handleConfirm={handleDelete}
      disabled={value.trim() !== memberEmail || isDeleting}
      confirmText={isDeleting ? t('common:states.deleting') : t('common:actions.delete')}
      title={
        <span className="text-destructive">
          <ExclamationTriangleIcon className="mr-1 inline-block stroke-destructive" width={18} height={18} />{' '}
          {t('members.deleteDialog.title')}
        </span>
      }
    >
      <Alert variant="destructive">
        <ExclamationTriangleIcon className="h-4 w-4" />
        <AlertTitle>{t('members.deleteDialog.warningTitle')}</AlertTitle>
        <AlertDescription>{t('members.deleteDialog.warningDescription')}</AlertDescription>
      </Alert>
      <div className="mt-4">
        <Label htmlFor="confirmDelete">
          <Trans
            i18nKey="members.deleteDialog.confirmInstruction"
            ns="settings"
            values={{ email: memberEmail }}
            components={{ bold: <span className="font-semibold" /> }}
          />
        </Label>
        <Input
          id="confirmDelete"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="mt-2"
          placeholder={t('members.deleteDialog.confirmPlaceholder')}
        />
      </div>
    </ConfirmDialog>
  );
}
